// Unified MediaPipe Pose runtime abstraction.
//
// Exposes one handle shape — { kind, delegate, detect, warmup, dispose } —
// regardless of *where* inference actually runs. The pose-worker-store walks a
// fallback ladder (Worker+GPU -> Worker+CPU -> main-thread+GPU -> main-thread+CPU)
// and hands the winning handle to the detection loop, which never has to know
// which rung succeeded.
//
// Why a main-thread rung exists: on some iOS Safari builds a Web Worker has no
// `OffscreenCanvas`, and @mediapipe/tasks-vision's WASM glue then falls back to
// `document.createElement`/`document.body` during graph setup (see
// public/wasm/vision_wasm_internal.js) — throwing "Can't find variable: document"
// for BOTH the GPU and CPU delegates. There is no way to recover from that
// inside the worker, so the only way to keep the exercise usable is to run the
// landmarker on the main thread, where `document` exists.

// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
// This module runs on the main thread only (createWorkerRuntime's own model
// fetch happens inside pose-landmarker.worker.js's separate execution
// context, which cannot import this store — that rung's download is measured
// here instead, from the 'progress'/'ready' messages it already posts back).
import { useNetworkDiagnostics } from '../zustand/network-diagnostics-store';

export const LOCAL_WASM_PATH = '/wasm';
export const LOCAL_HEAVY_MODEL_PATH = '/models/pose_landmarker_heavy.task';

// Kept in sync with the (previously in-worker) landmarker options.
const POSE_LANDMARKER_OPTIONS = {
  numPoses: 1,
  minPoseDetectionConfidence: 0.9,
  minTrackingConfidence: 0.9,
  minPosePresenceConfidence: 0.9,
  outputSegmentationMasks: false,
  runningMode: 'VIDEO',
};

// A worker rung that neither reports `ready` nor `error` (e.g. a WASM init that
// hangs, or an async failure the worker couldn't surface) must not stall the
// whole ladder on the loading screen forever — abandon it after this and let
// the store move on to the next rung. Generous because the heavy model may
// still be compiling on a low-end device on a cold cache.
const WORKER_INIT_TIMEOUT_MS = 25000;

// Fetch the model ourselves (instead of letting MediaPipe fetch modelAssetPath
// internally) so we can stream the body and report real byte-level download
// progress — createFromOptions gives no progress hook. Mirrors the worker's own
// copy; the returned bytes go to MediaPipe via baseOptions.modelAssetBuffer.
async function fetchModelWithProgress(url, onProgress) {
  // Diagnostics only — does not change what's fetched or how.
  const startedAt = performance.now();
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch model (${response.status})`);
  }

  const total = Number(response.headers.get('Content-Length')) || 0;

  const recordDownload = loadedBytes => {
    try {
      const record = useNetworkDiagnostics.getState().record;
      const durationMs = Math.round(performance.now() - startedAt);
      record('media', 'mediapipe_download_bytes', loadedBytes, 'bytes', { endpoint: url });
      record('media', 'mediapipe_download_duration_ms', durationMs, 'ms', { endpoint: url });
    } catch (_error) {
      // Never let instrumentation break the model load.
    }
  };

  if (!response.body || typeof response.body.getReader !== 'function') {
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    onProgress?.({ phase: 'model', loaded: bytes.length, total });
    recordDownload(bytes.length);
    return bytes;
  }

  const reader = response.body.getReader();
  const chunks = [];
  let loaded = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    chunks.push(value);
    loaded += value.length;
    onProgress?.({ phase: 'model', loaded, total });
  }

  const bytes = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  recordDownload(loaded);
  return bytes;
}

/**
 * Warm the browser cache with the model + WASM bytes without building a
 * PoseLandmarker. Called at app startup so the assets are already local by the
 * time the exercise page actually initializes a runtime. Never throws.
 */
export async function prefetchPoseAssets() {
  const urls = [
    `${LOCAL_WASM_PATH}/vision_wasm_internal.js`,
    `${LOCAL_WASM_PATH}/vision_wasm_internal.wasm`,
    LOCAL_HEAVY_MODEL_PATH,
  ];

  await Promise.all(
    urls.map(url =>
      // Read the body so the full response lands in the HTTP cache (a bare
      // fetch of a streamed body may not fully download).
      fetch(url)
        .then(response => (response.ok ? response.arrayBuffer() : null))
        .catch(() => null)
    )
  );
}

/**
 * Diagnostics only: stage durations of one worker round trip, from the
 * epoch-based timestamps on both sides (performance.timeOrigin + now(), since
 * the worker's performance.now() has its own origin). Both clocks are the same
 * system clock, but timeOrigin may be coarsened, so a stage a fraction of a
 * millisecond long can come out slightly negative — clamped to 0.
 */
function describeWorkerTiming(timing, postedAtEpochMs) {
  if (!timing) {
    return null;
  }
  const receivedAtEpochMs = performance.timeOrigin + performance.now();
  const nonNegative = value => (Number.isFinite(value) ? Math.max(0, value) : null);
  return {
    // postMessage -> worker handler start: transfer + worker task queue.
    transferToWorkerMs: nonNegative(timing.receivedAtEpochMs - postedAtEpochMs),
    // Handler start -> draw (the already-resolved ensureLandmarker await).
    queueWaitMs: nonNegative(timing.handlerWaitMs),
    // drawImage onto the worker's OffscreenCanvas.
    workerPreprocessMs: nonNegative(timing.drawMs),
    // Worker postMessage -> main-thread handler: structured clone + main-thread queue.
    workerReturnMs: nonNegative(receivedAtEpochMs - timing.postedAtEpochMs),
  };
}

/**
 * Worker runtime: PoseLandmarker lives in a dedicated Web Worker, inference
 * never touches the React main thread. Resolves once the worker reports `ready`
 * for the requested delegate; rejects (and tears the worker down) on error,
 * an uncaught worker error, or the init timeout — so the store can try the next
 * rung of the ladder.
 *
 * @param {{ delegate: 'GPU'|'CPU', onProgress?: (p:{phase:string,loaded:number,total:number}) => void }} opts
 * @returns {Promise<object>} runtime handle
 */
export function createWorkerRuntime({ delegate, onProgress }) {
  return new Promise((resolve, reject) => {
    let worker;
    try {
      worker = new Worker(new URL('./pose-landmarker.worker.js', import.meta.url));
    } catch (error) {
      reject(new Error(`Failed to construct pose worker: ${error?.message || error}`));
      return;
    }

    const pending = new Map(); // requestId -> resolve(detectResult)
    let requestId = 0;
    let settled = false;
    let timeoutId = null;
    // Diagnostics only — the worker posts 'progress' messages during its own
    // model fetch (pose-landmarker.worker.js), but that fetch happens inside
    // the worker's isolated scope, which has no access to this store. First
    // 'model'-phase progress message marks the download start; 'ready' marks
    // its end.
    let modelDownloadStartedAt = null;

    const cleanupInitListeners = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    // A stall timeout, not a fixed deadline: it restarts on every 'progress'
    // message, so a slow-but-downloading model (a cold cache — e.g. the first
    // load in a fresh private window, with nothing local to fall back on) is
    // never killed mid-download just for taking a while. Only a worker that
    // goes silent — no progress, no ready, no error — for the whole window
    // gets abandoned. Without this, an aborted-and-retried rung reset progress
    // to 0 and re-fetched the model from scratch on the next ladder rung,
    // which could hit the same wall again — the download visibly climbing
    // partway then dropping back to 0 on a loop.
    const armInitTimeout = () => {
      cleanupInitListeners();
      timeoutId = setTimeout(
        () => fail(`Pose worker init stalled for ${WORKER_INIT_TIMEOUT_MS}ms with no progress`),
        WORKER_INIT_TIMEOUT_MS
      );
    };

    const fail = message => {
      if (settled) {
        return;
      }
      settled = true;
      cleanupInitListeners();
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onWorkerError);
      worker.terminate();
      reject(new Error(message));
    };

    function onMessage(event) {
      const data = event.data || {};
      switch (data.type) {
        case 'progress':
          armInitTimeout();
          if (data.phase === 'model' && modelDownloadStartedAt === null) {
            modelDownloadStartedAt = performance.now();
          }
          if (data.phase === 'model' && data.loaded === data.total && data.total > 0) {
            try {
              const record = useNetworkDiagnostics.getState().record;
              record('media', 'mediapipe_download_bytes', data.loaded, 'bytes', {
                endpoint: LOCAL_HEAVY_MODEL_PATH,
              });
              if (modelDownloadStartedAt !== null) {
                record(
                  'media',
                  'mediapipe_download_duration_ms',
                  Math.round(performance.now() - modelDownloadStartedAt),
                  'ms',
                  { endpoint: LOCAL_HEAVY_MODEL_PATH }
                );
              }
            } catch (_error) {
              // Never let instrumentation break the ladder.
            }
          }
          onProgress?.(data);
          break;
        case 'ready':
          if (settled) {
            return;
          }
          settled = true;
          cleanupInitListeners();
          resolve(buildHandle());
          break;
        case 'error':
          // Post-ready errors (e.g. a global handler firing during a detect)
          // are ignored — only an error *during init* fails the rung.
          fail(data.message || 'Pose worker failed to initialize');
          break;
        case 'result':
        case 'dropped': {
          const settle = pending.get(data.requestId);
          if (settle) {
            pending.delete(data.requestId);
            settle(data);
          }
          break;
        }
        default:
          break;
      }
    }

    function onWorkerError(event) {
      fail(event?.message || 'Pose worker crashed during initialization');
    }

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onWorkerError);

    armInitTimeout();

    worker.postMessage({ type: 'init', delegate });

    function buildHandle() {
      return {
        kind: 'worker',
        delegate,
        // Diagnostics metadata: how a frame reaches the model on this rung.
        pipeline: {
          thread: 'worker',
          inputTransfer: 'transferred-imagebitmap',
          offscreenCanvas: true,
        },
        detect({ bitmap, timestampMs, inputWidth, inputHeight }) {
          return new Promise(res => {
            const id = ++requestId;
            const postedAtEpochMs = performance.timeOrigin + performance.now();
            pending.set(id, data => {
              if (data.type === 'dropped') {
                res({ dropped: true });
              } else {
                res({
                  durationMs: data.durationMs,
                  landmarks: data.landmarks ?? [],
                  worldLandmarks: data.worldLandmarks ?? [],
                  timing: describeWorkerTiming(data.timing, postedAtEpochMs),
                });
              }
            });
            worker.postMessage(
              { type: 'detect', requestId: id, bitmap, timestampMs, inputWidth, inputHeight },
              [bitmap]
            );
          });
        },
        warmup({ bitmap, timestampMs }) {
          worker.postMessage({ type: 'warmup', bitmap, timestampMs }, [bitmap]);
        },
        dispose() {
          worker.removeEventListener('message', onMessage);
          worker.removeEventListener('error', onWorkerError);
          worker.terminate();
          pending.clear();
        },
      };
    }
  });
}

/**
 * Main-thread runtime: PoseLandmarker runs inline on the React thread. Slower
 * (a heavy inference can jank a frame) but works where a worker can't build the
 * WASM graph at all (the iOS `document` failure). Used only as the last rungs
 * of the ladder.
 *
 * @param {{ delegate: 'GPU'|'CPU', onProgress?: (p:{phase:string,loaded:number,total:number}) => void }} opts
 * @returns {Promise<object>} runtime handle
 */
export async function createMainThreadRuntime({ delegate, onProgress }) {
  onProgress?.({ phase: 'wasm', loaded: 0, total: 0 });

  const { FilesetResolver, PoseLandmarker } = await import('@mediapipe/tasks-vision');
  const vision = await FilesetResolver.forVisionTasks(LOCAL_WASM_PATH);
  const modelBuffer = await fetchModelWithProgress(LOCAL_HEAVY_MODEL_PATH, onProgress);
  const landmarker = await PoseLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetBuffer: modelBuffer,
      delegate,
    },
    ...POSE_LANDMARKER_OPTIONS,
  });

  let lastTimestampMs = -1;
  let workCanvas = null;
  let workCtx = null;

  const nextTimestamp = timestampMs => {
    const next = timestampMs > lastTimestampMs ? timestampMs : lastTimestampMs + 1;
    lastTimestampMs = next;
    return next;
  };

  const ensureCanvas = (width, height) => {
    if (!workCanvas) {
      workCanvas =
        typeof OffscreenCanvas !== 'undefined'
          ? new OffscreenCanvas(width, height)
          : document.createElement('canvas');
      workCtx = workCanvas.getContext('2d');
    }
    if (workCanvas.width !== width) {
      workCanvas.width = width;
    }
    if (workCanvas.height !== height) {
      workCanvas.height = height;
    }
  };

  return {
    kind: 'main',
    delegate,
    // Diagnostics metadata: how a frame reaches the model on this rung.
    pipeline: {
      thread: 'main',
      inputTransfer: 'none',
      offscreenCanvas: typeof OffscreenCanvas !== 'undefined',
    },
    detect({ bitmap, timestampMs, inputWidth, inputHeight }) {
      try {
        const drawStartedAt = performance.now();
        ensureCanvas(inputWidth, inputHeight);
        workCtx.drawImage(bitmap, 0, 0, inputWidth, inputHeight);

        const ts = nextTimestamp(timestampMs);
        const startedAt = performance.now();
        let result;
        try {
          result = landmarker.detectForVideo(workCanvas, ts);
        } catch (error) {
          result = { landmarks: [], worldLandmarks: [] };
        }
        const durationMs = performance.now() - startedAt;

        return Promise.resolve({
          durationMs,
          landmarks: result?.landmarks ?? [],
          worldLandmarks: result?.worldLandmarks ?? [],
          // Diagnostics only — same shape as the worker rung; no thread hop here.
          timing: {
            transferToWorkerMs: 0,
            queueWaitMs: 0,
            workerPreprocessMs: startedAt - drawStartedAt,
            workerReturnMs: 0,
          },
        });
      } finally {
        bitmap.close();
      }
    },
    warmup({ bitmap, timestampMs }) {
      try {
        ensureCanvas(bitmap.width, bitmap.height);
        workCtx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height);
        landmarker.detectForVideo(workCanvas, nextTimestamp(timestampMs));
      } catch (error) {
        // Non-fatal — warm-up result is always discarded.
      } finally {
        bitmap.close();
      }
    },
    dispose() {
      try {
        landmarker.close?.();
      } catch (error) {
        // ignore
      }
    },
  };
}
